import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Maximize2,
  ArrowRight,
} from "lucide-react";

// Linear interpolation between keyframes
const interpolateKF = (keyframes, time) => {
  if (!keyframes || keyframes.length === 0) return null;
  if (keyframes.length === 1) return { x: keyframes[0].x, y: keyframes[0].y };
  const sorted = [...keyframes].sort((a, b) => a.time - b.time);
  if (time <= sorted[0].time) return { x: sorted[0].x, y: sorted[0].y };
  if (time >= sorted[sorted.length - 1].time)
    return { x: sorted[sorted.length - 1].x, y: sorted[sorted.length - 1].y };
  for (let i = 0; i < sorted.length - 1; i++) {
    if (time >= sorted[i].time && time <= sorted[i + 1].time) {
      const t = (time - sorted[i].time) / (sorted[i + 1].time - sorted[i].time);
      return {
        x: sorted[i].x + (sorted[i + 1].x - sorted[i].x) * t,
        y: sorted[i].y + (sorted[i + 1].y - sorted[i].y) * t,
      };
    }
  }
  return { x: sorted[sorted.length - 1].x, y: sorted[sorted.length - 1].y };
};

const OVERLAY_STYLES = {
  super: { color: "#ffffff", fontSize: "clamp(14px, 2.5vw, 22px)", fontWeight: "700", textShadow: "0 2px 8px rgba(0,0,0,0.8)" },
  disclaimer: { color: "#cccccc", fontSize: "clamp(9px, 1.4vw, 13px)", fontWeight: "400", textShadow: "0 1px 4px rgba(0,0,0,0.9)" },
  end_frame: { color: "#ffffff", fontSize: "clamp(16px, 3vw, 28px)", fontWeight: "800", textShadow: "0 2px 12px rgba(0,0,0,0.7)", letterSpacing: "0.05em" },
};

const PreviewPlayer = ({
  videoUrl,
  scenes,
  cropPositions,
  activeAspect,
  overlays,
  duration,
  projectId,
  sourceWidth,
  sourceHeight,
  onClose,
}) => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(duration || 0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  // Parse aspect ratio
  const [arW, arH] = useMemo(() => {
    if (!activeAspect) return [16, 9];
    const parts = activeAspect.split(":").map(Number);
    return parts.length === 2 ? parts : [16, 9];
  }, [activeAspect]);

  const targetRatio = arW / arH;
  const srcRatio = (sourceWidth || 1920) / (sourceHeight || 1080);

  // Video time update
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onTime = () => setCurrentTime(vid.currentTime);
    const onLoaded = () => setVideoDuration(vid.duration || duration || 0);
    const onEnded = () => setPlaying(false);
    vid.addEventListener("timeupdate", onTime);
    vid.addEventListener("loadedmetadata", onLoaded);
    vid.addEventListener("ended", onEnded);
    return () => {
      vid.removeEventListener("timeupdate", onTime);
      vid.removeEventListener("loadedmetadata", onLoaded);
      vid.removeEventListener("ended", onEnded);
    };
  }, [duration]);

  // Find current scene index
  const currentSceneIdx = useMemo(() => {
    for (let i = 0; i < scenes.length; i++) {
      if (currentTime >= scenes[i].start_time && currentTime < scenes[i].end_time) return i;
    }
    return scenes.length > 0 ? scenes.length - 1 : 0;
  }, [currentTime, scenes]);

  // Get crop position for current time (with keyframe interpolation)
  const cropPos = useMemo(() => {
    const scene = scenes[currentSceneIdx];
    if (!scene || !activeAspect) return { x: 50, y: 50 };
    const data = cropPositions[scene.id]?.[activeAspect];
    if (!data) return { x: 50, y: 50 };
    if (data.keyframes?.length >= 2) {
      const interp = interpolateKF(data.keyframes, currentTime);
      if (interp) return interp;
    }
    return { x: data.x ?? 50, y: data.y ?? 50 };
  }, [currentSceneIdx, currentTime, scenes, cropPositions, activeAspect]);

  // Calculate video transform inside the viewport
  const videoTransform = useMemo(() => {
    if (targetRatio < srcRatio) {
      // Crop horizontally (video wider than target)
      const videoW = (srcRatio / targetRatio) * 100;
      const excess = videoW - 100;
      const offsetX = -excess * (cropPos.x / 100);
      return { width: `${videoW}%`, height: "100%", left: `${offsetX}%`, top: "0%" };
    } else {
      // Crop vertically (video taller than target)
      const videoH = (targetRatio / srcRatio) * 100;
      const excess = videoH - 100;
      const offsetY = -excess * (cropPos.y / 100);
      return { width: "100%", height: `${videoH}%`, left: "0%", top: `${offsetY}%` };
    }
  }, [targetRatio, srcRatio, cropPos]);

  // Active overlays at current time
  const activeOverlays = useMemo(() => {
    if (!overlays) return [];
    return overlays.filter(
      (o) => currentTime >= o.start_time && currentTime <= o.end_time
    );
  }, [overlays, currentTime]);

  const togglePlay = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    if (playing) vid.pause();
    else vid.play();
    setPlaying(!playing);
  }, [playing]);

  const seekTo = useCallback((time) => {
    if (videoRef.current) videoRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const handleProgressClick = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(fraction * videoDuration);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Keyboard controls
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === " " || e.key === "k") { e.preventDefault(); togglePlay(); }
      if (e.key === "ArrowLeft") seekTo(Math.max(0, currentTime - 1));
      if (e.key === "ArrowRight") seekTo(Math.min(videoDuration, currentTime + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, togglePlay, seekTo, currentTime, videoDuration]);

  const formatTime = (t) => {
    const m = Math.floor(t / 60);
    const s = t % 60;
    return `${m}:${s < 10 ? "0" : ""}${s.toFixed(1)}`;
  };

  const progress = videoDuration > 0 ? (currentTime / videoDuration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col items-center justify-center"
      data-testid="preview-modal"
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-30">
        <div className="flex items-center gap-4">
          <div className="px-3 py-1 rounded-sm bg-cyan-500/10 border border-cyan-500/20">
            <span className="text-xs font-['Rajdhani'] tracking-wider text-cyan-300/80">
              PREVIEW — {activeAspect || "Original"}
            </span>
          </div>
          <span className="text-xs text-white/30 font-mono">
            Scene {currentSceneIdx + 1} / {scenes.length}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-white/40 hover:text-white transition-colors"
          data-testid="preview-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Viewport */}
      <div
        className="relative overflow-hidden bg-black shadow-[0_0_80px_rgba(0,180,255,0.08)] border border-white/[0.06] rounded-sm"
        style={{
          aspectRatio: `${arW}/${arH}`,
          maxHeight: "70vh",
          maxWidth: "85vw",
          width: targetRatio >= 1 ? "auto" : undefined,
          height: targetRatio < 1 ? "70vh" : "70vh",
        }}
        data-testid="preview-viewport"
      >
        {/* Video — scaled and positioned for crop */}
        <video
          ref={videoRef}
          src={videoUrl}
          className="absolute object-cover"
          style={{
            width: videoTransform.width,
            height: videoTransform.height,
            left: videoTransform.left,
            top: videoTransform.top,
          }}
          playsInline
          muted={false}
          data-testid="preview-video"
        />

        {/* Overlays */}
        {activeOverlays.map((ov) => {
          const style = OVERLAY_STYLES[ov.type] || OVERLAY_STYLES.super;
          const posX = ov.position?.x ?? 50;
          const posY = ov.position?.y ?? 50;
          // Fade in/out at edges
          const fadeIn = Math.min(1, (currentTime - ov.start_time) / 0.3);
          const fadeOut = Math.min(1, (ov.end_time - currentTime) / 0.3);
          const opacity = Math.min(fadeIn, fadeOut);

          return (
            <div
              key={ov.id}
              className="absolute pointer-events-none"
              style={{
                left: `${posX}%`,
                top: `${posY}%`,
                transform: "translate(-50%, -50%)",
                opacity,
                transition: "opacity 0.15s",
                ...style,
                maxWidth: "80%",
                textAlign: "center",
                lineHeight: 1.3,
              }}
              data-testid={`preview-overlay-${ov.id}`}
            >
              {ov.content}
            </div>
          );
        })}

        {/* Scene transition indicator */}
        <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 rounded-sm">
          <span className="text-[9px] text-white/40 font-mono">
            {formatTime(currentTime)}
          </span>
        </div>
      </div>

      {/* Transport controls */}
      <div className="w-full max-w-2xl mt-6 px-6" data-testid="preview-transport">
        {/* Progress bar */}
        <div
          ref={progressRef}
          className="relative h-1.5 bg-white/10 rounded-full cursor-pointer mb-4 group"
          onClick={handleProgressClick}
          data-testid="preview-progress"
        >
          <div
            className="absolute h-full bg-cyan-400/70 rounded-full transition-[width] duration-100"
            style={{ width: `${progress}%` }}
          />
          {/* Scene markers */}
          {scenes.map((s, i) => (
            <div
              key={i}
              className="absolute top-0 w-px h-full bg-white/20"
              style={{ left: `${(s.start_time / videoDuration) * 100}%` }}
            />
          ))}
          {/* Scrub handle */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-cyan-400 rounded-full shadow-[0_0_8px_rgba(0,200,255,0.5)] opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => seekTo(Math.max(0, scenes[Math.max(0, currentSceneIdx - 1)]?.start_time ?? 0))}
              className="p-2 text-white/40 hover:text-white transition-colors"
              data-testid="preview-prev"
            >
              <SkipBack className="w-4 h-4" />
            </button>
            <button
              onClick={togglePlay}
              className="p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
              data-testid="preview-play-btn"
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button
              onClick={() => {
                const next = Math.min(scenes.length - 1, currentSceneIdx + 1);
                seekTo(scenes[next]?.start_time ?? 0);
              }}
              className="p-2 text-white/40 hover:text-white transition-colors"
              data-testid="preview-next"
            >
              <SkipForward className="w-4 h-4" />
            </button>
            <span className="text-xs text-white/40 font-mono ml-2">
              {formatTime(currentTime)} / {formatTime(videoDuration)}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleFullscreen}
              className="p-2 text-white/30 hover:text-white transition-colors"
              data-testid="preview-fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                onClose();
                navigate(`/project/${projectId}/encoding`);
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-black font-['Rajdhani'] tracking-wider text-sm rounded-sm transition-colors"
              data-testid="preview-approve-btn"
            >
              APPROVE & ENCODE <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard hints */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <span className="text-[10px] text-white/15 font-mono">
          SPACE play/pause &nbsp; &larr;&rarr; skip &nbsp; ESC close &nbsp; F fullscreen
        </span>
      </div>
    </div>
  );
};

export default PreviewPlayer;
